import React, { useMemo } from 'react'
import { Popover, Spin } from 'antd'
import moment from 'moment'
import Widget from '../../../components/Widget'

const getGroupLabel = (item, selectedGroup) => {
  const itemMoment = item && item.date_timestamp
    ? moment(item.date_timestamp)
    : item && item.date
      ? moment(item.date)
      : null

  if (!itemMoment || !itemMoment.isValid()) return 'Unknown'

  if (selectedGroup === 'w') {
    const weekStart = itemMoment.clone().startOf('isoWeek')
    const weekEnd = itemMoment.clone().endOf('isoWeek')
    return `${weekStart.format('DD-MM')} to ${weekEnd.format('DD-MM')}`
  }

  if (selectedGroup === 'm') {
    const monthStart = itemMoment.clone().startOf('month')
    const monthEnd = itemMoment.clone().endOf('month')
    return `${monthStart.format('DD-MM')} to ${monthEnd.format('DD-MM')}`
  }

  if (selectedGroup === 'y') {
    const yearStart = itemMoment.clone().startOf('year')
    const yearEnd = itemMoment.clone().endOf('year')
    return `${yearStart.format('MM-YYYY')} to ${yearEnd.format('MM-YYYY')}`
  }

  return itemMoment.format('DD-MM-YYYY')
}

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const getOverspeedCount = (item) => {
  const summary = item && (
    item.overspeed_summary ||
    item.over_speed_summary ||
    item.overspeeding_summary ||
    item.overspeedSummary
  )

  if (summary) {
    if (summary.total_overspeed_events != null) return toNumber(summary.total_overspeed_events)
    if (summary.totalOverspeedEvents != null) return toNumber(summary.totalOverspeedEvents)
    if (Array.isArray(summary.overspeed_events)) return summary.overspeed_events.length
  }

  if (Array.isArray(item && item.overspeed_events)) return item.overspeed_events.length

  return Math.max(
    0,
    toNumber(item && item.total_overspeed_events),
    toNumber(item && item.overspeed_count),
    toNumber(item && item.overspeed_events_count),
    toNumber(item && item.overspeeding_count),
    toNumber(item && item.overSpeedCount),
    toNumber(item && item.over_speed_count)
  )
}

const getGreenDrivingBreakup = (item) => {
  const summary = item && (
    item.green_driving_summary ||
    item.green_drivng_summery ||
    item.greenDrivingSummary
  )

  let acceleration = 0
  let braking = 0
  let cornering = 0

  if (summary && summary.counts) {
    acceleration = toNumber(summary.counts[1] || summary.counts['1'] || summary.counts.acceleration)
    braking = toNumber(summary.counts[2] || summary.counts['2'] || summary.counts.braking)
    cornering = toNumber(summary.counts[3] || summary.counts['3'] || summary.counts.cornering)
  } else if (summary && summary.logs) {
    acceleration = Array.isArray(summary.logs[1] || summary.logs['1']) ? (summary.logs[1] || summary.logs['1']).length : 0
    braking = Array.isArray(summary.logs[2] || summary.logs['2']) ? (summary.logs[2] || summary.logs['2']).length : 0
    cornering = Array.isArray(summary.logs[3] || summary.logs['3']) ? (summary.logs[3] || summary.logs['3']).length : 0
  }

  if (!acceleration && !braking && !cornering) {
    acceleration = Math.max(
      0,
      toNumber(item && item.harsh_acceleration_count),
      toNumber(item && item.acceleration_count),
      toNumber(item && item.green_driving_acceleration_count)
    )
    braking = Math.max(
      0,
      toNumber(item && item.harsh_braking_count),
      toNumber(item && item.braking_count),
      toNumber(item && item.green_driving_braking_count)
    )
    cornering = Math.max(
      0,
      toNumber(item && item.harsh_cornering_count),
      toNumber(item && item.cornering_count),
      toNumber(item && item.green_driving_cornering_count)
    )
  }

  return {
    acceleration,
    braking,
    cornering
  }
}

const groupNameLabel = (selectedGroup) => {
  if (selectedGroup === 'w') return 'Weekly'
  if (selectedGroup === 'm') return 'Monthly'
  if (selectedGroup === 'y') return 'Yearly'
  return 'Daily'
}

const circleStyle = (count) => ({
  width: 96,
  height: 96,
  borderRadius: '50%',
  background: count > 0 ? '#f5222d' : '#1890ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 28,
  fontWeight: 700,
  cursor: 'pointer',
  userSelect: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.18)'
})

function SafetyEventCount(props) {
  const {
    data = [],
    selectedGroup = 'd',
    loading = false
  } = props

  const {
    overspeedBreakup,
    greenDrivingBreakup,
    overspeedTotal,
    greenDrivingTotal
  } = useMemo(() => {
    const groupOrder = []
    const grouped = {}

    data.forEach((item) => {
      const groupLabel = getGroupLabel(item, selectedGroup)
      if (!grouped[groupLabel]) {
        grouped[groupLabel] = {
          overspeed: 0,
          acceleration: 0,
          braking: 0,
          cornering: 0
        }
        groupOrder.push(groupLabel)
      }

      grouped[groupLabel].overspeed += getOverspeedCount(item)

      const green = getGreenDrivingBreakup(item)
      grouped[groupLabel].acceleration += green.acceleration
      grouped[groupLabel].braking += green.braking
      grouped[groupLabel].cornering += green.cornering
    })

    const overspeedBreakup = groupOrder.map((label) => ({
      label,
      count: grouped[label].overspeed
    }))

    const greenDrivingBreakup = groupOrder.map((label) => {
      const row = grouped[label]
      const total = row.acceleration + row.braking + row.cornering
      return {
        label,
        acceleration: row.acceleration,
        braking: row.braking,
        cornering: row.cornering,
        total
      }
    })

    return {
      overspeedBreakup,
      greenDrivingBreakup,
      overspeedTotal: overspeedBreakup.reduce((sum, row) => sum + row.count, 0),
      greenDrivingTotal: greenDrivingBreakup.reduce((sum, row) => sum + row.total, 0)
    }
  }, [data, selectedGroup])

  const overspeedPopup = (
    <div style={{ minWidth: 300, maxHeight: 320, overflowY: 'auto' }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Overspeeding ({groupNameLabel(selectedGroup)} breakup)</div>
      {(overspeedBreakup || []).length === 0 ? (
        <div>No data</div>
      ) : (
        (overspeedBreakup || []).map((row) => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span>{row.label}</span>
            <strong>{row.count}</strong>
          </div>
        ))
      )}
    </div>
  )

  const greenPopup = (
    <div style={{ minWidth: 420, maxHeight: 320, overflowY: 'auto' }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Green Driving ({groupNameLabel(selectedGroup)} breakup)</div>
      {(greenDrivingBreakup || []).length === 0 ? (
        <div>No data</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr .7fr .7fr .7fr .7fr', fontWeight: 700, paddingBottom: 6 }}>
            <span>Group</span>
            <span>Accel</span>
            <span>Brake</span>
            <span>Corner</span>
            <span>Total</span>
          </div>
          {(greenDrivingBreakup || []).map((row) => (
            <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1.4fr .7fr .7fr .7fr .7fr', padding: '4px 0' }}>
              <span>{row.label}</span>
              <span>{row.acceleration}</span>
              <span>{row.braking}</span>
              <span>{row.cornering}</span>
              <strong>{row.total}</strong>
            </div>
          ))}
        </>
      )}
    </div>
  )

  return (
    <Widget>
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 56, flexWrap: 'wrap', width: '100%' }}>
          <div style={{ minWidth: 220, maxWidth: 280, flex: '0 1 260px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: 10, fontWeight: 600 }}>Overspeeding Count</div>
            <Popover content={overspeedPopup} title={null} trigger="click" placement="top">
              <div style={circleStyle(overspeedTotal)}>{overspeedTotal}</div>
            </Popover>
          </div>
          <div style={{ minWidth: 220, maxWidth: 280, flex: '0 1 260px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: 10, fontWeight: 600 }}>Green Driving Count</div>
            <Popover content={greenPopup} title={null} trigger="click" placement="top">
              <div style={circleStyle(greenDrivingTotal)}>{greenDrivingTotal}</div>
            </Popover>
          </div>
        </div>
      </Spin>
    </Widget>
  )
}

export default SafetyEventCount
